import "./style/drawer-popup.scss"

import React, { useCallback, useEffect, useId } from "react"
import classNames from "classnames"
import { useDispatch, useSelector } from "react-redux"
import { popupScrollPositionChange } from "@app/store/app/appActions"
import { RootState } from "@app/types"
import { AppDispatch } from "@ng-mw/shared-react-components/src/types/types"
import { StaticBox } from "@modules/shared/Grid"
import ModalDrawer, { type ModalDrawerProps } from "@ng-mw/reol/components/ModalDrawer"
import PopupButtons from "./PopupButtons"
import PopupHeader from "./PopupHeader"
import PopupTitle from "./PopupTitle"
import type { BackgroundEnum } from "./Popup"

export interface DrawerPopupProps {
    buttons?: React.ReactNode | React.JSX.Element
    children?: React.ReactNode
    className?: string
    drawerSize?: ModalDrawerProps["size"]
    isHeaderActionsOverflow?: boolean
    isOpen: boolean
    isSimple?: boolean
    shouldCloseOnOverlayClick?: boolean
    shouldCloseOnSwipeDown?: boolean
    isTitleHidden?: boolean
    onClose?: () => void
    onGoBack?: () => void
    title?: string | React.ReactNode
    titleIllustration?: React.ReactNode
    backgroundColor?: BackgroundEnum
    hideCloseButton?: boolean
}

const DrawerPopup = ({
    buttons,
    children,
    className,
    drawerSize = "tall",
    isHeaderActionsOverflow,
    isOpen,
    isSimple,
    shouldCloseOnOverlayClick = true,
    shouldCloseOnSwipeDown = true,
    isTitleHidden,
    onClose,
    onGoBack,
    title,
    titleIllustration,
    backgroundColor,
    hideCloseButton,
}: DrawerPopupProps) => {
    const id = useId()
    const dispatch = useDispatch<AppDispatch>()
    const popupScrollPosition = useSelector((state: RootState) => state.app.popupScrollPosition)

    useEffect(() => {
        if (isOpen) {
            dispatch(popupScrollPositionChange(true, false))
        }
    }, [dispatch, isOpen])

    const onScroll = useCallback(
        (event: React.UIEvent<HTMLDivElement>) => {
            const scrollTop = event.currentTarget.scrollTop
            const atTop = scrollTop <= 15
            const goingDown = scrollTop > 10

            if (atTop && !popupScrollPosition.top) {
                dispatch(popupScrollPositionChange(true, false))
            }

            if (goingDown && !popupScrollPosition.goingDown && !atTop) {
                dispatch(popupScrollPositionChange(false, true))
            }
        },
        [dispatch, popupScrollPosition.goingDown, popupScrollPosition.top],
    )

    return (
        <ModalDrawer
            ariaLabel={title ? undefined : "Popup"}
            ariaLabelledBy={title ? id : undefined}
            className={classNames(className, {
                [`app-modal-drawer--${backgroundColor}`]: backgroundColor,
            })}
            footer={
                buttons ? (
                    <StaticBox>
                        <PopupButtons>{buttons}</PopupButtons>
                    </StaticBox>
                ) : null
            }
            header={
                <PopupHeader
                    ariaControls={id}
                    isActionsOverflow={isHeaderActionsOverflow}
                    onClose={onClose}
                    onGoBack={onGoBack}
                    title={title}
                    hideCloseButton={hideCloseButton}
                />
            }
            isOpen={isOpen}
            onClose={onClose}
            onScroll={onScroll}
            shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
            shouldCloseOnSwipeDown={shouldCloseOnSwipeDown}
            size={drawerSize}
        >
            {title ? (
                <PopupTitle
                    id={id}
                    illustration={titleIllustration}
                    isHidden={isTitleHidden}
                    isSimple={isSimple}
                >
                    {title}
                </PopupTitle>
            ) : null}
            {children}
        </ModalDrawer>
    )
}

export default DrawerPopup
